import type { SentinelAiEventLogWireDto, SentinelWorkspaceDetailWireDto, TraceStep } from '@/modules/sentinel-workspace/types';

const INTERNAL_TRACE_NAMES = new Set(['fill_template', 'replan', 'ask_customer']);
const POLICY_NODE_TYPES = new Set([
  'llm_decide',
  'condition',
  'planning',
  'policy_tool',
  'policy_state',
  'orchestrator',
  'v3_orchestrator',
  'v3_stage',
  'react_state',
  'rejection',
]);
const COMPOSE_NODE_TYPES = new Set(['respond', 'llm_generate', 'fill_template', 'template']);
/** The step is finding information — even when it is not a tool call, like a workflow search. */
const LOOKUP_KEYWORDS = /\b(search|look|check|fetch|retriev)/i;
/** The step is deciding what to do. */
const POLICY_KEYWORDS = /\b(plan|polic|review|decid)/i;

export function isInternalTraceStep(input: { toolName?: string | null; nodeId?: string | null }): boolean {
  return Boolean(
    (input.toolName && INTERNAL_TRACE_NAMES.has(input.toolName)) ||
      (input.nodeId && INTERNAL_TRACE_NAMES.has(input.nodeId)),
  );
}

export function classifyTraceStepKind(input: {
  nodeType?: string | null;
  toolName?: string | null;
  nodeId?: string | null;
  label?: string | null;
}): TraceStep['kind'] {
  const nodeType = (input.nodeType ?? '').toLowerCase();
  if (input.toolName || nodeType === 'tool_call') return 'lookup';
  if (POLICY_NODE_TYPES.has(nodeType)) return 'policy';
  if (COMPOSE_NODE_TYPES.has(nodeType)) return 'compose';

  // Match the wording the sentinel reads; node ids (`policy_tool_call_…`) are only a
  // fallback for unlabelled steps because they would push everything to 'policy'.
  const text = input.label || input.nodeId || '';
  if (LOOKUP_KEYWORDS.test(text)) return 'lookup';
  return POLICY_KEYWORDS.test(text) ? 'policy' : 'compose';
}

function readString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function readRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function readOutcome(value: unknown): TraceStep['outcome'] {
  return value === 'success' || value === 'error' || value === 'awaiting_approval' ? value : undefined;
}

/** An approved tool reruns under its own node, so the resume replaces the row that stalled on it. */
function withoutResumedPauses(steps: TraceStep[]): TraceStep[] {
  const resumed = (step: TraceStep): boolean => step.id.slice(step.id.lastIndexOf(':') + 1).startsWith('approval_resume_');
  // The rerun repeats the arguments, which is what tells two approvals of one tool apart.
  const sameCall = (a: TraceStep, b: TraceStep): boolean =>
    a.toolName === b.toolName && JSON.stringify(a.toolInput ?? null) === JSON.stringify(b.toolInput ?? null);
  return steps.filter(
    (step, index) =>
      step.outcome !== 'awaiting_approval' ||
      !steps.some((later, at) => at > index && sameCall(step, later) && resumed(later)),
  );
}

export function traceStepsFromEvents(events: unknown, idPrefix = ''): TraceStep[] {
  if (!Array.isArray(events)) return [];

  const steps: TraceStep[] = [];
  const seenNodes = new Set<string>();
  const endedNodes = new Map<string, Record<string, unknown>>();
  for (const event of events) {
    if (!event || typeof event !== 'object' || Array.isArray(event)) continue;
    const record = event as Record<string, unknown>;
    if (record['type'] !== 'thinking_node_end') continue;
    const payload = readRecord(record['payload']);
    const nodeId = readString(payload?.['node_id']);
    if (nodeId && payload) endedNodes.set(nodeId, payload);
  }
  for (const event of events) {
    if (!event || typeof event !== 'object' || Array.isArray(event)) continue;
    const record = event as Record<string, unknown>;
    const type = record['type'];
    if (type !== 'thinking_node_start' && type !== 'thinking_node_end') continue;
    const payload = record['payload'];
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) continue;

    const value = payload as Record<string, unknown>;
    if (type === 'thinking_node_end') {
      continue;
    }
    const nodeId = readString(value['node_id']) ?? `step-${steps.length + 1}`;
    if (seenNodes.has(nodeId)) continue;
    if (isInternalTraceStep({ toolName: readString(value['tool_name']), nodeId: readString(value['node_id']) })) continue;

    const nodeLabel = readString(value['node_label']);
    const thinkingText = readString(value['thinking_text']);
    const title = nodeLabel ?? thinkingText ?? 'AI step';
    seenNodes.add(nodeId);
    const completed = endedNodes.get(nodeId);
    const durationMs = completed?.['duration_ms'];
    steps.push({
      id: idPrefix ? `${idPrefix}:${nodeId}` : nodeId,
      kind: classifyTraceStepKind({
        nodeType: readString(value['node_type']),
        toolName: readString(value['tool_name']),
        nodeId,
        label: title,
      }),
      title,
      detail: thinkingText && thinkingText !== title ? thinkingText : null,
      done: completed !== undefined,
      ...(readString(value['tool_name']) ? { toolName: readString(value['tool_name'])! } : {}),
      ...(readString(value['tool_provider']) ? { toolProvider: readString(value['tool_provider'])! } : {}),
      ...(readRecord(value['tool_args']) ? { toolInput: readRecord(value['tool_args']) } : {}),
      ...(completed && 'tool_output' in completed ? { toolOutput: completed['tool_output'] } : {}),
      ...(completed && readOutcome(completed['outcome']) ? { outcome: readOutcome(completed['outcome']) } : {}),
      ...(typeof durationMs === 'number' ? { durationMs } : {}),
    });
  }
  for (const [nodeId, value] of endedNodes) {
    const toolName = readString(value['tool_name']);
    if (seenNodes.has(nodeId) || !toolName || isInternalTraceStep({ toolName, nodeId })) continue;
    const title = readString(value['node_label']) ?? readString(value['thinking_text']) ?? toolName.replaceAll('_', ' ');
    const durationMs = value['duration_ms'];
    steps.push({
      id: idPrefix ? `${idPrefix}:${nodeId}` : nodeId,
      kind: classifyTraceStepKind({
        nodeType: readString(value['node_type']),
        toolName,
        nodeId,
        label: title,
      }),
      title,
      detail: null,
      done: true,
      toolName,
      ...(readString(value['tool_provider']) ? { toolProvider: readString(value['tool_provider'])! } : {}),
      ...('tool_output' in value ? { toolOutput: value['tool_output'] } : {}),
      ...(readOutcome(value['outcome']) ? { outcome: readOutcome(value['outcome']) } : {}),
      ...(typeof durationMs === 'number' ? { durationMs } : {}),
    });
  }
  return withoutResumedPauses(steps);
}

export function inflightTraceFromApi(dto: SentinelWorkspaceDetailWireDto): TraceStep[] {
  const traceId = dto.ticket.active_generation_trace_id;
  if (!traceId) return [];
  const event = dto.aiEventLogs.find((item) => item.trace_id === traceId);
  return traceStepsFromEvents(event?.extra_details?.['sentinelEvents'], event?.request_id ?? traceId);
}

function byCreation(left: SentinelAiEventLogWireDto, right: SentinelAiEventLogWireDto): number {
  return left.created_at.localeCompare(right.created_at) || left.id - right.id;
}

function eventTraceSteps(event: SentinelAiEventLogWireDto): TraceStep[] {
  return traceStepsFromEvents(event.extra_details?.['sentinelEvents'], event.request_id ?? event.trace_id ?? String(event.id));
}

/** Every event's steps in order — data fetched early in the ticket stays available to the cards. */
export function traceHistoryFromApi(dto: SentinelWorkspaceDetailWireDto): TraceStep[] {
  return [...dto.aiEventLogs].sort(byCreation).flatMap(eventTraceSteps);
}

export function mergeTraceSteps(persisted: TraceStep[], live: TraceStep[]): TraceStep[] {
  const liveByNode = new Map(live.map((step) => [step.id, step]));
  const persistedNodes = new Set(persisted.map((step) => step.id));
  const merged = persisted.map((step) => liveByNode.get(step.id) ?? step);
  return [...merged, ...live.filter((step) => !persistedNodes.has(step.id))];
}
